import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./Playground.module.css";
// Hardcoded for demo; in production, securely distribute this key
const ENCRYPTION_KEY =
  "a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2";
const WS_BASE_URL = "ws://localhost:8080/api/playground/ws";
const SAMPLE_RATE = 48000;
const SEND_CHUNK_MS = 40;
const PLAYBACK_LATENCY = 0.18;

// Convert hex string to ArrayBuffer
async function hexToArrayBuffer(hex) {
  try {
    if (!hex || typeof hex !== "string" || !/^[0-9a-fA-F]{64}$/.test(hex)) {
      throw new Error("Invalid hex key format");
    }
    const bytes = new Uint8Array(
      hex.match(/.{1,2}/g).map((byte) => parseInt(byte, 16))
    );
    return bytes.buffer;
  } catch (err) {
    console.error("hexToArrayBuffer error:", err.message, { hex });
    throw new Error("Failed to convert hex to ArrayBuffer: " + err.message);
  }
}

// Validate base64 string
function isValidBase64(str, name) {
  try {
    if (!str || typeof str !== "string") {
      throw new Error(`${name} is not a string`);
    }
    if (!/^[A-Za-z0-9+/=]+$/.test(str)) {
      throw new Error(`${name} contains invalid base64 characters`);
    }
    atob(str); // Test decoding
    return true;
  } catch (err) {
    console.error("Invalid base64 for", name, ":", str, err.message);
    return false;
  }
}

// Decrypt the callId
async function decryptCallId(encrypted, iv, authTag) {
  try {
    console.log("Decrypting with:", { encrypted, iv, authTag });

    // Validate inputs
    if (!encrypted || !iv || !authTag) {
      throw new Error("Missing encrypted, iv, or authTag");
    }
    if (!isValidBase64(encrypted, "encrypted")) {
      throw new Error("Invalid base64 format for encrypted");
    }
    if (!isValidBase64(iv, "iv")) {
      throw new Error("Invalid base64 format for iv");
    }
    if (!isValidBase64(authTag, "authTag")) {
      throw new Error("Invalid base64 format for authTag");
    }

    const key = await crypto.subtle.importKey(
      "raw",
      await hexToArrayBuffer(ENCRYPTION_KEY),
      { name: "AES-GCM" },
      false,
      ["decrypt"]
    );

    let ivBuf, encryptedBuf, authTagBuf;
    try {
      ivBuf = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0)).buffer;
      encryptedBuf = Uint8Array.from(atob(encrypted), (c) =>
        c.charCodeAt(0)
      ).buffer;
      authTagBuf = Uint8Array.from(atob(authTag), (c) =>
        c.charCodeAt(0)
      ).buffer;
    } catch (err) {
      console.error("Base64 decoding error:", err.message, {
        encrypted,
        iv,
        authTag,
      });
      throw new Error("Failed to decode base64: " + err.message);
    }

    console.log("Buffer lengths:", {
      encryptedBuf: encryptedBuf.byteLength,
      authTagBuf: authTagBuf.byteLength,
    });

    if (
      !(encryptedBuf instanceof ArrayBuffer) ||
      !(authTagBuf instanceof ArrayBuffer)
    ) {
      console.error("Invalid buffer types:", {
        encryptedBufType: typeof encryptedBuf,
        authTagBufType: typeof authTagBuf,
      });
      throw new Error("Invalid buffer types for encrypted or authTag");
    }

    let ciphertext;
    try {
      ciphertext = new Uint8Array(
        encryptedBuf.byteLength + authTagBuf.byteLength
      );
    } catch (err) {
      console.error("Failed to create ciphertext array:", err.message, {
        encryptedBufLength: encryptedBuf.byteLength,
        authTagBufLength: authTagBuf.byteLength,
      });
      throw new Error("Failed to create ciphertext array: " + err.message);
    }

    try {
      ciphertext.set(new Uint8Array(encryptedBuf), 0);
      ciphertext.set(new Uint8Array(authTagBuf), encryptedBuf.byteLength);
    } catch (err) {
      console.error("Ciphertext set error:", err.message, {
        ciphertextLength: ciphertext.length,
        encryptedBuf,
        authTagBuf,
      });
      throw new Error("Failed to set ciphertext: " + err.message);
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: ivBuf },
      key,
      ciphertext
    );
    const result = new TextDecoder().decode(decrypted);
    console.log("Decrypted callId:", result);
    return result;
  } catch (err) {
    console.error("Decryption error:", err.message, { encrypted, iv, authTag });
    throw new Error("Decryption failed: " + err.message);
  }
}

export default function Playground() {
  const location = useLocation();
  const [systemPrompt, setSystemPrompt] = useState(
    location.state?.prompt || "You are a helpful assistant..."
  );
  const [status, setStatus] = useState("idle");
  const [logs, setLogs] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(
    location.state?.voice || ""
  );

  console.log(location.state);
  console.log(selectedVoice);
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const micNodeRef = useRef(null);
  const workletURLRef = useRef(null);
  const playHeadRef = useRef(0);
  const gainNodeRef = useRef(null);

  const pushLog = (t) =>
    setLogs((s) =>
      [`${new Date().toLocaleTimeString()} • ${t}`, ...s].slice(0, 200)
    );

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(
      "https://prod.toxboxai.com/api/voice/get-voice-by-display-name",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setVoices(data);
          if (!location.state?.voice && data.length > 0)
            setSelectedVoice(data[0].displayName); // Updated to use displayName
        }
      })
      .catch((err) => {
        console.error("Error fetching voices:", err);
        pushLog(`Failed to fetch voices: ${err.message}`);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const id = location.state?.prompt; // assuming this is the prompt ID

    if (!id) return; // stop if no ID is passed

    fetch(
      `https://prod.toxboxai.com/api/prompts/${id}`,
      {
        // ✅ template literal
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data && data) {
          // adjust to your API's field name
          setSystemPrompt(data.prompt.promptDescription);
        }
      })
      .catch((err) => {
        console.error("Error fetching prompt:", err);
        pushLog(`Failed to fetch prompt: ${err.message}`);
      });
  }, []);

  useEffect(() => {
    const code = `
      class MicProcessor extends AudioWorkletProcessor {
        constructor() {
          super();
          this.sampleRate = sampleRate;
          this.buf = new Float32Array(0);
          this.port.onmessage = (e) => {
            if (e.data && e.data.chunkSamples) {
              this.chunkSamples = e.data.chunkSamples;
            }
          };
        }
        process(inputs) {
          const input = inputs[0];
          if (!input || !input[0]) return true;
          const channel = input[0];
          const newBuf = new Float32Array(this.buf.length + channel.length);
          newBuf.set(this.buf, 0);
          newBuf.set(channel, this.buf.length);
          this.buf = newBuf;
          if (this.chunkSamples && this.buf.length >= this.chunkSamples) {
            const out = new Int16Array(this.chunkSamples);
            for (let i = 0; i < this.chunkSamples; i++) {
              let s = Math.max(-1, Math.min(1, this.buf[i]));
              out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            this.buf = this.buf.subarray(this.chunkSamples);
            this.port.postMessage(out.buffer, [out.buffer]);
          }
          return true;
        }
      }
      registerProcessor('mic-aggregator', MicProcessor);
    `;
    const blob = new Blob([code], { type: "application/javascript" });
    workletURLRef.current = URL.createObjectURL(blob);
    return () => {
      if (workletURLRef.current) URL.revokeObjectURL(workletURLRef.current);
    };
  }, []);

  function pcm16ToAudioBuffer(arrayBuffer, sampleRate = SAMPLE_RATE) {
    const view = new DataView(arrayBuffer);
    const samples = view.byteLength / 2;
    const floats = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
      const v = view.getInt16(i * 2, true);
      floats[i] = v / 0x8000;
    }
    const ac = audioCtxRef.current;
    const buffer = ac.createBuffer(1, floats.length, sampleRate);
    buffer.getChannelData(0).set(floats);
    return buffer;
  }

  async function resampleIfNeeded(audioBuffer, targetRate) {
    if (Math.abs(audioBuffer.sampleRate - targetRate) < 1) return audioBuffer;
    const channels = audioBuffer.numberOfChannels;
    const length = Math.ceil(audioBuffer.duration * targetRate);
    const offline = new OfflineAudioContext(channels, length, targetRate);
    const bufferSrc = offline.createBufferSource();
    bufferSrc.buffer = audioBuffer;
    bufferSrc.connect(offline.destination);
    bufferSrc.start(0);
    const rendered = await offline.startRendering();
    return rendered;
  }

  async function schedulePlayback(audioBuffer) {
    const ac = audioCtxRef.current;
    if (!ac) return;
    const targetRate = ac.sampleRate;
    let buf = audioBuffer;
    if (Math.abs(buf.sampleRate - targetRate) > 1) {
      buf = await resampleIfNeeded(buf, targetRate);
    }
    if (!playHeadRef.current || playHeadRef.current < ac.currentTime + 0.05) {
      playHeadRef.current = ac.currentTime + PLAYBACK_LATENCY;
    }
    const startTime = playHeadRef.current;
    const src = ac.createBufferSource();
    src.buffer = buf;
    const g = gainNodeRef.current || ac.createGain();
    g.gain.setValueAtTime(1, startTime);
    src.connect(g).connect(ac.destination);
    src.start(startTime);
    playHeadRef.current += buf.duration;
  }

  async function handleWsMessage(event) {
    if (typeof event.data === "string") {
      try {
        const obj = JSON.parse(event.data);
        if (obj.type === "transcript") {
          pushLog(`[transcript ${obj.role}] ${obj.text || obj.delta || ""}`);
        } else if (obj.type === "state") {
          pushLog(`[state] ${obj.state}`);
        } else if (obj.type === "audio" && obj.data) {
          const b64 = obj.data;
          if (!isValidBase64(b64, "audio data")) {
            pushLog("Invalid base64 audio data received");
            return;
          }
          const bytes = Uint8Array.from(atob(b64), (c) =>
            c.charCodeAt(0)
          ).buffer;
          try {
            const decoded = await audioCtxRef.current.decodeAudioData(
              bytes.slice(0)
            );
            await schedulePlayback(decoded);
          } catch {
            const ab = pcm16ToAudioBuffer(bytes, SAMPLE_RATE);
            await schedulePlayback(ab);
          }
        } else {
          pushLog(JSON.stringify(obj));
        }
      } catch (err) {
        pushLog("Text message: " + event.data);
      }
      return;
    }

    let arrayBuffer;
    if (event.data instanceof Blob)
      arrayBuffer = await event.data.arrayBuffer();
    else arrayBuffer = event.data;

    try {
      const decoded = await audioCtxRef.current.decodeAudioData(
        arrayBuffer.slice(0)
      );
      await schedulePlayback(decoded);
      return;
    } catch (err) {
      try {
        const abuff = pcm16ToAudioBuffer(arrayBuffer, SAMPLE_RATE);
        await schedulePlayback(abuff);
      } catch (err) {
        pushLog("Audio decode failed: " + (err.message || err));
      }
    }
  }

  const startCall = async () => {
    if (status === "running") return;
    setStatus("requesting");
    pushLog("Requesting callId from backend...");
    try {
      const res = await fetch(
        "https://prod.toxboxai.com/api/playground/start-call",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ systemPrompt, displayName: selectedVoice }), // Updated to send displayName
        }
      );
      if (!res.ok) {
        const txt = await res.text();
        pushLog(`Backend error: ${res.status} ${txt}`);
        setStatus("error");
        return;
      }
      const json = await res.json();
      console.log("Backend response:", json);
      const { encryptedCallId, iv, authTag } = json;
      if (!encryptedCallId || !iv || !authTag) {
        pushLog("Missing encryptedCallId, iv, or authTag from server");
        setStatus("error");
        return;
      }

      const callId = await decryptCallId(encryptedCallId, iv, authTag);
      console.log("Decrypted callId:", callId);

      const joinUrl = `${WS_BASE_URL}?callId=${encodeURIComponent(callId)}`;
      pushLog(`Connecting to WebSocket: ${joinUrl}`);

      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({
          sampleRate: SAMPLE_RATE,
        });
      gainNodeRef.current = audioCtxRef.current.createGain();
      gainNodeRef.current.gain.value = 1.0;

      const ws = new WebSocket(joinUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      let pingInterval;
      ws.onopen = async () => {
        pushLog("Connected to WebSocket");
        setStatus("running");
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping"); // Send a custom 'ping' message for keep-alive
          }
        }, 30000);

        try {
          await audioCtxRef.current.audioWorklet.addModule(
            workletURLRef.current
          );
          const chunkSamples = Math.round((SEND_CHUNK_MS / 1000) * SAMPLE_RATE);
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { sampleRate: SAMPLE_RATE },
          });
          const srcNode = audioCtxRef.current.createMediaStreamSource(stream);
          const workletNode = new AudioWorkletNode(
            audioCtxRef.current,
            "mic-aggregator"
          );
          workletNode.port.postMessage({ chunkSamples });
          workletNode.port.onmessage = (evt) => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(evt.data);
              } catch (e) {
                pushLog(`Failed to send audio data: ${e.message}`);
              }
            }
          };
          srcNode.connect(workletNode);
          micNodeRef.current = { node: workletNode, stream };
          pushLog("Mic streaming (AudioWorklet) started");
        } catch (err) {
          pushLog(
            "AudioWorklet failed, falling back to MediaRecorder: " +
            (err.message || err)
          );
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          const mime =
            MediaRecorder.isTypeSupported &&
              MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
              ? "audio/webm;codecs=opus"
              : "";
          const mediaRecorder = new MediaRecorder(
            stream,
            mime ? { mimeType: mime } : undefined
          );
          mediaRecorder.ondataavailable = (e) => {
            if (
              ws &&
              ws.readyState === WebSocket.OPEN &&
              e.data &&
              e.data.size > 0
            ) {
              ws.send(e.data);
            }
          };
          mediaRecorder.start(250);
          micNodeRef.current = { recorder: mediaRecorder, stream };
          pushLog("Mic streaming (MediaRecorder) started");
        }
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          console.log("Received pong from server");
          return;
        }
        handleWsMessage(event);
      };
      ws.onerror = (err) => {
        pushLog(
          `WebSocket error: ${err.message || "Unknown error (check console)"}`
        );
        console.error("WebSocket error:", err);
      };
      ws.onclose = (event) => {
        pushLog(
          `WebSocket closed: code=${event.code}, reason=${event.reason || "none"
          }`
        );
        cleanupAudio();
        setStatus("idle");
        clearInterval(pingInterval);
      };
    } catch (err) {
      pushLog(`startCall failed: ${err.message || err}`);
      console.error("startCall error:", err);
      setStatus("error");
    }
  };

  function cleanupAudio() {
    try {
      if (micNodeRef.current) {
        if (micNodeRef.current.node) {
          micNodeRef.current.node.disconnect();
        }
        if (
          micNodeRef.current.recorder &&
          micNodeRef.current.recorder.state === "recording"
        ) {
          micNodeRef.current.recorder.stop();
        }
        if (micNodeRef.current.stream) {
          micNodeRef.current.stream.getTracks().forEach((t) => t.stop());
        }
      }
    } catch (e) {
      pushLog(`Cleanup audio error: ${e.message}`);
    }
    try {
      audioCtxRef.current?.close();
    } catch (e) {
      pushLog(`Audio context close error: ${e.message}`);
    }
    micNodeRef.current = null;
    audioCtxRef.current = null;
    playHeadRef.current = 0;
  }

  const stopCall = () => {
    try {
      wsRef.current?.close();
    } catch (e) {
      pushLog(`Error closing WebSocket: ${e.message}`);
    }
    cleanupAudio();
    setStatus("idle");
    pushLog("Call stopped by user");
  };

  return (
    <div style={{ padding: 18, fontFamily: "Segoe UI, Roboto, Arial" }}>
      <h1 className={styles.maintext}>Playground</h1>

      <div className={styles.modalContent}>
        <h3 className={styles.maintext}>
          Ultravox Playground (send + receive audio)
        </h3>

        <textarea
          className={styles.textArea}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={4}
          style={{ width: "100%", padding: 8 }}
        />

        <div className={styles.dropdownWrapper}>
          <label className={styles.dropdownLabel}>
            {/* Select Voice: */}
            <select
              value={selectedVoice}
              onChange={(e) => setSelectedVoice(e.target.value)}
              disabled={status === "running" || status === "requesting"}
              className={styles.dropdown}
            >
              {voices.length === 0 ? (
                <option value="">Loading voices...</option>
              ) : (
                <>
                  {voices.map((voice) => (
                    <option
                      key={voice._id || voice.displayName}
                      value={voice.displayName}
                    >
                      {voice.displayName}
                    </option>
                  ))}
                </>
              )}
            </select>
          </label>
          <span className={styles.callStatus}>Status : {status}</span>
          {status === "running" ? (
            <button
              className={styles.callButton}
              onClick={stopCall}
              disabled={status === "requesting"}
            >
              End Call
            </button>
          ) : (
            <button
              className={styles.callButton}
              onClick={startCall}
              disabled={status === "requesting" || !selectedVoice}
            >
              Start Call
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
